/**
 * Хук для загрузки общей статистики Instagram аккаунтов за выбранный период.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchAccountInsightsBatch } from '../api/account-insights';
import { toInstagramSince, toInstagramUntil } from '../lib/date-range';
import { computeEr, computeErByReach } from '../lib/er';
import { useGeneralStatsCache } from '../store/generalStatsCache';

import type { AccountStatsRow, ResolvedDateRange } from '../types/account-stats';

function safeDelta(curr: number | null, prev: number | null): number | null {
  if (curr === null || prev === null) return null;
  return curr - prev;
}

type State = {
  rows: AccountStatsRow[];
  isLoading: boolean;
  isFetching: boolean;
};

export function useAccountsGeneralStats(
  dateRange: ResolvedDateRange,
  comparePrev: boolean
): {
  rows: AccountStatsRow[];
  isLoading: boolean;
  isFetching: boolean;
  refetch: (opts?: { forceRefresh?: boolean }) => void;
} {
  const since = toInstagramSince(dateRange.from);
  const until = toInstagramUntil(dateRange.to);
  const prevSince =
    comparePrev && dateRange.prevFrom ? toInstagramSince(dateRange.prevFrom) : undefined;
  const prevUntil =
    comparePrev && dateRange.prevTo ? toInstagramUntil(dateRange.prevTo) : undefined;

  const cacheKey = `${since}_${until}_${comparePrev ? `prev_${prevSince ?? ''}_${prevUntil ?? ''}` : 'noprev'}`;

  const [state, setState] = useState<State>(() => {
    const cached = useGeneralStatsCache.getState();
    if (cached.cacheKey === cacheKey) {
      return { rows: cached.rows, isLoading: false, isFetching: false };
    }
    return { rows: [], isLoading: true, isFetching: false };
  });
  const abortRef = useRef<AbortController | null>(null);
  const forceRefreshRef = useRef(false);

  const doFetch = useCallback(
    (forceRefresh?: boolean) => {
      if (!forceRefresh) {
        const cached = useGeneralStatsCache.getState();
        if (cached.cacheKey === cacheKey) {
          setState({ rows: cached.rows, isLoading: false, isFetching: false });
          return () => {};
        }
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setState(prev => ({ ...prev, isLoading: prev.rows.length === 0, isFetching: true }));

      fetchAccountInsightsBatch({ since, until, prevSince, prevUntil, forceRefresh })
        .then(data => {
          if (controller.signal.aborted) return;

          const rows: AccountStatsRow[] = data.rows.map(row => {
            const er = computeEr(row.impressions, row.interactions);
            const prevEr = comparePrev ? computeEr(row.prev_impressions, row.prev_interactions) : null;
            const erByReach = computeErByReach(row.reach, row.interactions);
            const prevErByReach = comparePrev ? computeErByReach(row.prev_reach, row.prev_interactions) : null;

            return {
              accountId: row.account_id,
              username: row.username,
              name: row.name,
              avatarUrl: row.avatar_url,
              tags: row.tags ?? [],
              followers: row.followers_count,
              impressions: row.impressions,
              reach: row.reach,
              interactions: row.interactions,
              er,
              erByReach,
              followersDelta: row.followers_delta,
              impressionsDelta: comparePrev ? safeDelta(row.impressions, row.prev_impressions) : null,
              reachDelta: comparePrev ? safeDelta(row.reach, row.prev_reach) : null,
              interactionsDelta: comparePrev
                ? safeDelta(row.interactions, row.prev_interactions)
                : null,
              erDelta: comparePrev ? safeDelta(er, prevEr) : null,
              erByReachDelta: comparePrev ? safeDelta(erByReach, prevErByReach) : null,
              isLoading: false,
              error: row.error,
            };
          });

          setState({ rows, isLoading: false, isFetching: false });
          useGeneralStatsCache.getState().setCache(cacheKey, rows);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          console.error('[useAccountsGeneralStats] fetch error:', err);
          setState(prev => ({ ...prev, isLoading: false, isFetching: false }));
        });

      return () => controller.abort();
    },
    [since, until, prevSince, prevUntil, comparePrev, cacheKey]
  );

  useEffect(() => {
    return doFetch();
  }, [doFetch]);

  const refetch = useCallback(
    (opts?: { forceRefresh?: boolean }) => {
      doFetch(opts?.forceRefresh);
    },
    [doFetch]
  );

  return {
    rows: state.rows,
    isLoading: state.isLoading,
    isFetching: state.isFetching,
    refetch,
  };
}
